import { useState, useCallback } from 'react';
import { SuperTooltipTemplate } from './types';
import { _noop } from '@client/new/helpers/general';
import { useSuperTooltipModel } from './super-tooltip.model';
import { SuperTooltipTemplateId } from './generated';
import { trackEvent } from '@services/tracking';

type SuperTooltipVMProps = {
  templateId: SuperTooltipTemplateId;
  slideIndex?: number;
  hideNavigation?: boolean;
  wrapperClassName?: string;
  showDivider?: boolean;
};

export type SuperTooltipVM = {
  templates: SuperTooltipTemplate[];
  currentIndex: number;
  hideNavigation: boolean;
  isOpen: boolean;
  wrapperClassName?: string;
  showDivider: boolean;
  onGoToNext: () => void;
  onGoToPrev: () => void;
  onClose: () => void;
  onOpen: () => void;
  onGoToSlide: (index: number) => void;
  onLinkClicked: (link: string) => void;
};

export const useSuperTooltipVM = ({
  templateId,
  slideIndex = 0,
  hideNavigation = false,
  wrapperClassName,
  showDivider = false,
}: SuperTooltipVMProps): SuperTooltipVM => {
  const [isOpen, setIsOpen] = useState(false);
  const model = useSuperTooltipModel({
    templateId,
  });
  const templates = model.content;
  const [currentIndex, setCurrentIndex] = useState(slideIndex);

  const onGoToNext = useCallback(() => {
    if (currentIndex < templates.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      trackEvent({
        type: 'Super Tooltip slide navigation',
      });
    }
  }, [currentIndex, templates.length]);

  const onGoToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  }, [currentIndex]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    setCurrentIndex(0);
  }, []);

  const onOpen = useCallback(() => {
    trackEvent({
      type: 'Super Tooltip Click',
      payload: {
        'Tooltip Type': templateId,
      },
    });
    setIsOpen(true);
  }, []);

  const onLinkClicked = (link: string) => {
    trackEvent({
      type: 'Super Tooltip internal link click',
      payload: {
        Link: link,
      },
    });
    window.open(link, '_blank');
  };

  const onGoToSlide = (index: number) => {
    if (index >= 0 && index < templates.length) {
      setCurrentIndex(index);
    }
  };

  return {
    templates,
    currentIndex,
    hideNavigation,
    onGoToNext,
    onGoToPrev,
    onClose,
    onOpen,
    onLinkClicked,
    onGoToSlide,
    isOpen,
    wrapperClassName,
    showDivider,
  };
};
